<template>
	<div class="xs-12 flex wrap xs-center">
		<div class="xs-12 max6 mb10">
			<!-- Headline -->
			<div class="flex xs-between xs-middle xs-12 ta1 fs5 fw7 lh125 mt8 mb4">
				<div>{{ $t("update") }} {{ currentPermissionGroup.name }}</div>
				<div>
					<j-button type="tertiary" @click="$router.back()">{{ $t("back") }}</j-button>
				</div>
			</div>

			<!-- List of permissions -->
			<j-card class="xs-12 bg5 r2 p4">
				<div
					v-for="permission in computedPermissions"
					:key="permission.id"
					class="flex xs-between xs-middle mb4"
				>
					<span>{{ permission.id ? permission.id : permission.name }}</span>
					<j-base-multi-select
						class="xs-5"
						size="small"
						:options="permission.options"
						:value="permission.targetType"
						@input="handleInput({ permission: permission, payload: $event })"
					/>
				</div>

				<!-- Loading -->
				<div v-if="isLoading" class="p4 ta2">{{ $t("loading") }}</div>
			</j-card>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton/"
import JBaseMultiSelect from "@/components/JBaseMultiSelect/"
import JCard from "@/components/JCard/"
import api from "@/modules/users/api/"

export default {
	components: {
		JButton,
		JCard,
		JBaseMultiSelect
	},

	props: {
		/**
		 * Id of permissionGroup user wants to update
		 */
		id: {
			type: String,
			required: true
		}
	},

	data() {
		return {
			/**
			 * List of all available permissions
			 * Is fetched once in created and is cached here
			 */
			allPermissions: [],

			/**
			 * Permission group that users is currently updating
			 */

			currentPermissionGroup: {},

			isLoading: false
		}
	},

	computed: {
		myPermissions() {
			return this.$store.state.permissions
				.filter(item => item.permission.id !== "clientId")
				.filter(item => item.permission.id !== "groups")
				.filter(item => item.permission.id !== "role")
		},

		computedPermissions() {
			// Go through list of all permissions
			// Find if particular permissions is assigned to currentPermissionGroup
			// If it is, append property isSelected=true so that we can show it's added
			// Also figure out what targetIds permission has so that we know if it's "all" or "" version
			return this.myPermissions.map(myPermission => {
				// Find if permission from allPermissions is in the currentPermissionGroup
				const found = this.currentPermissionGroup.targettedPermissions
					? this.currentPermissionGroup.targettedPermissions.find(
							item => item.permission.id === myPermission.permission.id
					  )
					: null

				// Return enhanced permission object
				// if permission exist in currentGroup, it will be _isSelected = true
				// if it doesn't exist, it will be _isSelected = false
				return {
					id: myPermission.permission.id,
					name: myPermission.permission.name,
					description: myPermission.permission.description,
					options: this.decideOptionsAvailable(myPermission.targetIds),
					targetType:
						found && found.targetIds && found.targetIds.length
							? found?.targetIds
							: null
				}
			})
		}
	},

	methods: {
		async getAllPermissions() {
			this.isLoading = true
			const allPermissions = await api.readAllPermissions()
			this.isLoading = false

			if (allPermissions.errors) {
				this.$store.dispatch("showError", allPermissions.errors)
				this.allPermissions = []
				return
			}

			this.allPermissions = allPermissions.data.permissions
		},

		async getPermissionsOfGroup() {
			// Get all permissions groups and find the one user is updating based on id
			// Api doesn't support filtering so we have to fetch all permission groups
			this.isLoading = true
			const permissionGroups = await api.readAllPermissionGroups()
			this.isLoading = false

			if (permissionGroups.errors) {
				this.$store.dispatch("showError", permissionGroups.errors)
				return
			}

			// Find current permission group based on this.$route.params.id
			const currentPermissionGroup = permissionGroups.data.permissionGroups.find(
				item => item.id === this.$route.params.id
			)
			if (!currentPermissionGroup) return

			this.currentPermissionGroup = currentPermissionGroup
		},

		async addPermission(permissionId, targetId) {
			const res = await api.addPermissionToPermissionGroup({
				id: this.$route.params.id, // id of permission group
				permissionId,
				targetId
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)
		},

		async removePermission(permissionId, targetId) {
			const res = await api.removePermissionFromPermissionGroup({
				id: this.$route.params.id, // id of permission group
				permissionId,
				targetId
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)
		},

		async handleInput({ permission, payload }) {
			// targetIds of the current permission
			const currentTargetIds = this.currentPermissionGroup?.targettedPermissions?.find(
				item => item.permission.id === permission.id
			)?.targetIds

			if (!currentTargetIds) {
				await this.addPermission(permission.id, payload?.[0])
			} else {
				// newly selected targetIds from user selection
				const newTargetIds = payload

				// compare two arrays above by getting difference in two arrays
				// get the added or removed item
				const removedValue = currentTargetIds?.filter(
					x => !newTargetIds?.includes(x)
				)
				const addedValue = newTargetIds?.filter(
					x => !currentTargetIds?.includes(x)
				)

				if (removedValue?.length > 0) {
					console.log("removing permission")
					await this.removePermission(permission.id, removedValue[0])
				} else if (addedValue?.length > 0) {
					console.log("adding permission")
					await this.addPermission(permission.id, addedValue[0])
				}
			}

			this.getPermissionsOfGroup()
		},

		// check my permission, and decide what permissions to be selectable
		decideOptionsAvailable(targetIds) {
			let output = [
				{
					name: "{creatorRights}",
					value: "{creatorRights}"
				},
				{
					name: "{entityId}",
					value: "{entityId}"
				}
			]

			if (targetIds?.includes("all")) {
				output.push({
					name: "all",
					value: "all"
				})
			}

			return output
		}
	},

	mounted() {
		this.getAllPermissions()
		this.getPermissionsOfGroup()
	}
}
</script>
